<template>
  <app-modal v-model="isDialogOpened"
             :ok-disabled="isConfirmUpgradeDisabled"
             :ok-loading="okLoading"
             :ok-text="$t('common.confirm')"
             size="md"
             @cancel="closeDialog"
             @ok="onConfirmUpgrade"
  >
    <template #title>
      {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.title', { name: room.name }) }}
    </template>
    <template #body>
      <p class="heading-sm-bold text-text-primary">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferTitle') }}</p>

      <div class="offer text-text-secondary">
        <div class="offer__details">
          <div>
            <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferDataroomLabel"
                    tag="p"
                    class="heading-sm-bold"
                    scope="global"
            >
              <template #storage>
                {{ getDisplaySize(room.dataroomInfo.initialStorageGb) }}
              </template>
              <template #duration>
                <span v-t="{
                  path: 'project.project-settings.ProjectSettingsDataroomUpgradeDialog.monthsCount',
                  plural: room.dataroomInfo.initialDuration,
                  args: { months: room.dataroomInfo.initialDuration },
                }" />
              </template>
            </i18n-t>

            <p class="mt-2 text--x-small">
              {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.subscriptionDateLabel', { date: ISOToDDMMYYYY(room.createdAt)}) }}
            </p>
          </div>
          <span class="ml-auto body-lg-bold">{{ getDisplayPrice(room.dataroomInfo.monthlyPrice) }}</span>
          <span class="body-lg-bold">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}</span>
        </div>
        <div v-for="(addon, index) in room.dataroomInfo.addOns" :key="index" class="offer__details">
          <div>
            <p class="heading-sm-bold">
              {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.currentOfferAddonLabel')}} - {{ getDisplaySize(addon.sizeGb) }}
            </p>
            <p class="text--x-small">
              {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.subscriptionDateLabel', { date: ISOToDDMMYYYY(addon.date)}) }}
            </p>
          </div>
          <span class="ml-auto body-lg-bold">{{ getDisplayPrice(addon.monthlyPrice) }}</span>
          <span class="body-lg-bold">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}</span>
        </div>

        <!-- Total Monthly Price -->
        <div class="offer__details">
          <p class="text--small">
            {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.total') }}
          </p>
          <span class="ml-auto text--small">
            {{ getDisplayPrice(totalMonthlyPrice) }}
          </span>
          <span class="text--x-small">
            {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
          </span>
        </div>
      </div>

      <div class="d-flex justify-space-between align-center mb-2">
        <span class="heading-sm-bold text-text-primary">{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageTitle') }}</span>
        <v-chip v-if="computedCustomerDiscountPercent"
                color="surface-protege-subtle"
                class="font-weight-medium px-2 rounded-xl text-text-protege"
                label
                size="small"
        >
          {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.customerDiscount', { percent: computedCustomerDiscountPercent }) }}
        </v-chip>
      </div>

      <p class="text-text-secondary mb-4 text--small">
        {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSubTitle') }}
      </p>

      <v-radio-group v-model="v$.selectedStorageQuantity.$model"
                     hide-details
                     class="addons"
      >
        <v-radio v-for="nb in addonsDisplayedNumber"
                 :key="nb"
                 :value="nb"
                 class="addon"
                 :class="{ 'addon--active': selectedStorageQuantity === nb }"
        >
          <template #label>
            <span class="addon__size text--medium text-text-secondary">
              {{ getDisplaySize(room.dataroomInfo.addonStorage * nb) }}
            </span>
            <div class="ml-auto">
              <template v-if="computedCustomerDiscountPercent">
                <span class="text-text-secondary text-decoration-line-through mr-2 text--small">
                  {{ getDisplayPrice(room.dataroomInfo.addonPrice * nb) }}
                  {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                </span>
              </template>
              <span class="body-lg-bold text-text-secondary">
                {{ getDisplayPrice(room.dataroomInfo.addonPriceWithDiscount * nb) }}
              </span>
              <span class="body-lg-bold text-text-secondary">
                {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
              </span>
            </div>
          </template>
        </v-radio>
        <v-radio :value="selectedStorageWiderQuantity"
                 class="addon"
                 :class="{ 'addon--active': selectedStorageQuantity && selectedStorageQuantity === selectedStorageWiderQuantity }"
        >
          <template #label>
            <span class="addon__size pa-0 text--medium">
              <AppSelect v-model="selectedStorageWiderQuantity"
                         :items="computedStorageWiderItems"
                         :placeholder="$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStoragePlaceholder')"
                         class="addon__select"
                         noLabel
                         @update:model-value="onStorageWiderQuantityChange"
              />
            </span>
            <div>
              <template v-if="computedCustomerDiscountPercent && selectedStorageWiderQuantity">
                <span class="text-text-secondary text-decoration-line-through mr-2 text--small">
                  {{ getDisplayPrice(room.dataroomInfo.addonPrice * selectedStorageWiderQuantity) }}{{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
                </span>
              </template>
              <span class="ml-auto body-lg-bold text-text-secondary">{{ computedStorageWiderDisplayPrice }}</span>
              <span class="body-lg-bold text-text-secondary">
                {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.perMonth') }}
              </span>
            </div>
          </template>
        </v-radio>
      </v-radio-group>

      <div v-if="selectedStorageQuantity" class="mt-4">
        <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSumUpText"
                tag="p"
                class="text--medium text-text-secondary"
                scope="global"
        >
          <template #price>
            <span class="text--small text--bold text-text-secondary">
              {{ totalAdditionalStorageDisplayPrice }}
            </span>
          </template>
        </i18n-t>
      </div>

      <v-checkbox v-model="v$.termsChecked.$model"
                  :error-messages="termsCheckedError"
                  class="terms__checkbox"
      >
        <template #label>
          <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsLabel"
                  tag="span"
                  scope="global"
          >
            <template #link>
              <AppDownloadFile :link="termsLink">
                <template #default="{ submitForm }">
                  <span class="text-text-link text-decoration-underline"
                        @click="submitForm"
                  >
                    {{ $t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsLabelLink') }}
                  </span>
                </template>
              </AppDownloadFile>
            </template>
            </i18n-t>
          </template>
        </v-checkbox>
     </template>
   </app-modal>
 </template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { defineComponent } from 'vue'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppSelect from '@/common/AppSelect.vue'
import { ISOToDDMMYYYY } from '@/common/utils/dates'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'

export default defineComponent({
  name: 'ProjectSettingsDataroomUpgradeDialog',
  components: { AppDownloadFile, AppSelect },
  setup: () => ({ v$: useVuelidate() }),
  mixins: [dialogMixin],
  props: {
    room: {
      type: Object,
      required: true,
    },
    submitAction: {
      type: Function,
      required: true,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'refresh-stats'],
  data () {
    return {
      ISOToDDMMYYYY,
      addonsDisplayedNumber: 3,
      addonsMaxNumber: 9,
      selectedStorageQuantity: null,
      selectedStorageWiderQuantity: null,
      termsChecked: false,
    }
  },
  computed: {
    okLoading () {
      return this.submitLoading
    },
    totalMonthlyPrice () {
      let total = Number.parseFloat(this.room.dataroomInfo.monthlyPrice)

      this.room.dataroomInfo.addOns.forEach((addon) => {
        total += Number.parseFloat(addon.monthlyPrice)
      })

      return total
    },
    computedCustomerDiscountPercent () {
      const { addonPrice, addonPriceWithDiscount } = this.room.dataroomInfo

      // addonPrice must be positive to avoid dividing by zero.
      // addonPriceWithDiscount can be 0 for a 100% discount, but must not exceed addonPrice.
      if (addonPrice == null || addonPrice <= 0
        || addonPriceWithDiscount == null || addonPriceWithDiscount < 0 || addonPriceWithDiscount > addonPrice) {
        return 0
      }

      const discount = addonPrice - addonPriceWithDiscount
      return Math.round((discount / addonPrice) * 100)
    },
    computedStorageWiderItems () {
      const items = []

      for (let i = this.addonsDisplayedNumber; i < this.addonsMaxNumber; i++) {
        const item = {
          title: `${this.getDisplaySize(this.room.dataroomInfo.addonStorage * (i + 1))}`,
          value: i + 1,
        }
        items.push(item)
      }

      return items
    },
    computedStorageWiderDisplayPrice () {
      if (this.selectedStorageWiderQuantity) {
        return this.getDisplayPrice(this.room.dataroomInfo.addonPriceWithDiscount * this.selectedStorageWiderQuantity)
      }

      return this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice', { price: '- ' })
    },
    isConfirmUpgradeDisabled () {
      return this.v$.$invalid
    },
    termsCheckedError () {
      return formFieldErrors(
        this.v$.termsChecked,
        [
          ['sameAs', this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.termsValidationError')],
        ],
      )
    },
    termsLink () {
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=dataroom-price-list-${this.$i18n.locale.toUpperCase()}`
    },
    totalAdditionalStorageSize () {
      return (this.selectedStorageQuantity * this.room.dataroomInfo.addonStorage).toFixed(1)
    },
    totalAdditionalStorageDisplayPrice () {
      return this.getDisplayPrice(this.selectedStorageQuantity * this.room.dataroomInfo.addonPriceWithDiscount)
    },
  },
  methods: {
    getDiscountPrice (price, discountPercent) {
      const discount = (price * discountPercent) / 100
      return price - discount
    },
    getDisplayPrice (price, discountPercent) {
      price = Number.parseFloat(price)
      const finalPrice = discountPercent ? this.getDiscountPrice(price, discountPercent) : price
      return this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice', { price: this.$n(finalPrice, 'decimal') })
    },
    getDisplaySize (size) {
      const formattedSize = Number.parseFloat(Number(size).toFixed(1))

      if (formattedSize < 1) {
        return `${formattedSize * 1000} ${this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.sizeUnit.Mb')}`
      } else {
        return `${formattedSize} ${this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.sizeUnit.Gb')}`
      }
    },
    async onConfirmUpgrade () {
      try {
        await this.submitAction({
          storage: this.totalAdditionalStorageSize,
          unit: 'Gb',
        })

        this.$emit('refresh-stats')
        toast.success(this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageSuccessText', {
          name: this.room.name,
        }))
        this.closeDialog()
      } catch (e) {
        console.error('Error while adding storage addon:', e)
        toast.error(this.$t('project.project-settings.ProjectSettingsDataroomUpgradeDialog.addStorageErrorText'))
      }
    },
    onStorageWiderQuantityChange () {
      this.selectedStorageQuantity = this.selectedStorageWiderQuantity
    },
  },
  validations: {
    selectedStorageQuantity: { required },
    termsChecked: { sameAs: sameAs(true) },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.offer {
  background-color: rgb(var(--v-theme-surface-tertiary));
  @include border-radius('border-radius-2');
  padding: 16px;
  margin-bottom: 24px;
  margin-top: 16px;

  &__details {
    align-items: center;
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: get-border('border-secondary');
      margin-bottom: 16px;
      padding-bottom: 8px;
    }
  }
}

.addons {
  display: block;

  :deep(.v-input__control) {
    display: block;

    .v-selection-control-group {
      row-gap: 8px;
    }

    .v-field__outline__start,
    .v-field__outline__end {
      border: none;
    }
  }
}

.addon {
  display: block;
  margin: 0 !important;
  @include border-radius('border-radius-3');
  cursor: pointer;
  outline: 1px solid rgb(var(--v-theme-border-secondary));
  outline-offset: -1px;

  &--active {
    outline-color: rgb(var(--v-theme-border-info-bold));
    outline-width: 2px;
  }

  &__size {
    background-color: rgb(var(--v-theme-surface-tertiary));
    @include border-radius('border-radius-2');
    padding: 8px;
    min-width: 110px;
    text-align: center;
  }

  &__select {
    min-width: 200px;
  }

  :deep(.v-selection-control__wrapper) {
    display: none;
  }

  :deep(.closd-select .v-field) {
    background-color: transparent;
  }

  :deep(.v-label) {
    width: 100%;
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
  }
}

.terms__checkbox {
   margin-top: 24px;
   padding-top: 0;

  :deep(.v-input__slot) {
    align-items: start;
  }
}
</style>
